<template>
  <div class="ask-ai-input-wrapper">
    <h3 class="title">
      <img :src="require('@/assets/img/work-flow-icons/magic-stick.svg')" />
      <span>Bearbeite mit AI</span>
    </h3>
    <div class="prompt-wrapper">
      <div
        ref="editable"
        class="editable"
        contenteditable="true"
        @input="onInput"
        @keydown="onKeydown"
        @keyup="onKeyup"
        @click="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @paste="onPaste"
        @copy="onCopy"
        @cut="onCut"
        data-placeholder="Beschreibe was du ändernoder umsetzen möchtest"
      ></div>

      <ul
        v-if="showVariableDropdown"
        class="dropdown"
        :style="{
          top: variableDropdownPos.top + 'px',
          left: variableDropdownPos.left + 'px',
        }"
      >
        <li
          v-for="(item, index) in filteredVariables"
          :key="item.name + 'variable' + index"
          class="suggest-variable"
          :class="{ selected: index === selectedIndex }"
          @mousedown.prevent="onSuggestionClick(item)"
        >
          {{ item.label }}
        </li>
      </ul>

      <button
        class="submit-btn"
        :disabled="isLoading || !plainText?.trim()"
        @click="submitPrompt"
      >
        <b-icon
          icon="arrow-up-short"
          style="color: #fff; font-weight: 700"
        ></b-icon>
      </button>
      <div v-if="isLoading" class="loading-spinner">
        <LoadingSpinner />
      </div>
    </div>
    <div
      ref="suggetionsWrapper"
      class="suggetions-wrapper"
      :style="suggetionDropdownPosStyle"
      v-show="isSuggestionDropdownVisible"
    >
      <AskAiSuggetionDropdown
        :optionsGroup="getCurrentFormSuggetions"
        :inputVariables="getInputVariables"
        @select="onSelectSuggetion"
      />
    </div>
  </div>
</template>

<script>
import LoadingSpinner from '@/components/widgets/LoadingSpinnerV2.vue';
import {
  getInputVariableList,
  getElementSuggetionById,
} from '@/utils/helpers/FormBuilderHelper.js';
import AskAiSuggetionDropdown from '@/components/FormBuilder/AskAI/AskAiSuggetionDropdown.vue';

import { mapGetters } from 'vuex';
export default {
  name: 'AskAiInput',
  data() {
    return {
      isFocused: false,
      prompt: '',
      plainText: '',
      currentToken: '',
      tokenStartIndex: 0,
      caretOffset: 0,
      showVariableDropdown: false,
      hideDropdownTimer: null,
      variableDropdownPos: { top: 0, left: 0 },
      suggetionDropdownPosStyle: {},
      selectedIndex: -1,
    };
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    elements: {
      type: Array,
      required: true,
    },
    processId: {
      type: String | Number,
      required: true,
    },
    selectedId: {
      type: String,
      default: '',
    },
    taskId: {
      type: String | Number,
      required: true,
    },
  },

  methods: {
    async submitPrompt() {
      await this.$listeners.submit?.(this.prompt);
      if (this.$refs.editable?.innerHTML) {
        this.$refs.editable.innerHTML = '';
      }
    },
    onSelectSuggetion(option) {
      this.prompt = option.value;
      this.renderDefaultValue(this.prompt);
      // this.isFocused = false;
      // this.$refs.editable.blur();
      // this.submitPrompt();
    },
    getPlainText() {
      return this.$refs.editable.innerText || '';
    },
    getCaretCharacterOffset() {
      const sel = window.getSelection();
      if (!sel || !sel.anchorNode || sel.rangeCount === 0) return 0;
      const range = sel.getRangeAt(0).cloneRange();
      range.selectNodeContents(this.$refs.editable);
      range.setEnd(sel.anchorNode, sel.anchorOffset);

      return range.toString().length;
    },
    getPromptText() {
      const nodes = this.$refs.editable.childNodes;
      let result = '';
      nodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          result += node.textContent;
        } else if (node.classList && node.classList.contains('chip')) {
          const name = node.getAttribute('data-name');
          result += `{${name}}`;
        }
      });
      return result.trim();
    },
    onInput() {
      this.caretOffset = this.getCaretCharacterOffset();
      const plain = this.getPlainText();
      const prefix = plain.slice(0, this.caretOffset);
      // last whitespace token (single word) fallback
      const tokenMatch = prefix.match(/(?:^|\s)([^\s]*)$/);
      const tokenFound = tokenMatch ? tokenMatch[1] : '';
      const tokenForMatch = tokenFound.replace(/^{/, '');
      // default start index for the token = caret - length_of_last_token
      let newTokenStartIndex = Math.max(
        0,
        this.caretOffset - tokenFound.length
      );
      let newToken = tokenForMatch;
      // CASE A: trailing space (tokenForMatch === "") but dropdown visible:
      // keep previous token + start index so user can continue typing (space + next word)
      if (tokenForMatch === '' && this.currentToken) {
        // CHANGED: removed && this.showVariableDropdown
        const alt = plain.slice(this.tokenStartIndex, this.caretOffset).trim(); // NEW: check if content still matches (handles backspace removal)
        if (alt === this.currentToken) {
          newToken = this.currentToken;
          if (typeof this.tokenStartIndex === 'number') {
            newTokenStartIndex = this.tokenStartIndex;
          }
        } else {
          newToken = '';
          newTokenStartIndex = this.caretOffset;
        }
      } else if (
        // CASE B: dropdown visible AND we have a previous tokenStartIndex: try using
        typeof this.tokenStartIndex === 'number' &&
        this.tokenStartIndex <= this.caretOffset
      ) {
        const alt = plain.slice(this.tokenStartIndex, this.caretOffset).trim();
        if (alt.length > 0 && alt.length >= tokenForMatch.length) {
          newToken = alt.replace(/^{/, '');
          newTokenStartIndex = this.tokenStartIndex;
        } else {
          newToken = tokenForMatch;
          newTokenStartIndex = Math.max(
            0,
            this.caretOffset - tokenFound.length
          );
        }
      } else {
        newToken = tokenForMatch;
        newTokenStartIndex = Math.max(0, this.caretOffset - tokenFound.length);
      }
      this.currentToken = newToken;
      this.tokenStartIndex = newTokenStartIndex;
      let q = (this.currentToken || '').toLowerCase();
      let tempFiltered = this.getInputVariables.filter(
        (v) =>
          v.label.toLowerCase().startsWith(q) ||
          v.name.toLowerCase().startsWith(q)
      );
      if (tempFiltered.length === 0 && tokenForMatch.length > 0) {
        this.currentToken = tokenForMatch;
        this.tokenStartIndex = Math.max(
          0,
          this.caretOffset - tokenFound.length
        );
      }
      this.showVariableDropdown =
        (this.currentToken || '').trim().length > 0 &&
        this.filteredVariables.length > 0;
      const exact = this.getInputVariables.find(
        (v) =>
          v.label.toLowerCase() === this.currentToken.toLowerCase() ||
          v.name.toLowerCase() === this.currentToken.toLowerCase()
      );
      if (exact && plain[this.caretOffset - 1] === ' ') {
        this.showVariableDropdown = false;
        this.tokenStartIndex = this.caretOffset;
      }
      if (this.showVariableDropdown) {
        const trailingMatch = prefix.match(/\s*$/);
        const trailingSpaces = trailingMatch ? trailingMatch[0].length : 0;
        if (trailingSpaces > 0) {
          const extendedQ = this.currentToken + ' '.repeat(trailingSpaces);
          const hasExtension = this.getInputVariables.some(
            (v) =>
              v.label.toLowerCase().startsWith(extendedQ.toLowerCase()) ||
              v.name.toLowerCase().startsWith(extendedQ.toLowerCase())
          );
          if (!hasExtension) {
            this.showVariableDropdown = false;
          }
        }
      }
      if (!this.showVariableDropdown && this.currentToken.trim().length > 0) {
        if (
          plain[this.tokenStartIndex - 1] === ' ' ||
          this.tokenStartIndex === 0
        ) {
          let spaceBefore = this.tokenStartIndex - 2;
          if (spaceBefore >= 0) {
            const prevSpacePos = plain.lastIndexOf(' ', spaceBefore);
            const prevStart = prevSpacePos !== -1 ? prevSpacePos + 1 : 0;
            const alt = plain.slice(prevStart, this.caretOffset).trim();
            const tempQ = alt.toLowerCase();
            const tempFiltered = this.getInputVariables.filter(
              (v) =>
                v.label.toLowerCase().startsWith(tempQ) ||
                v.name.toLowerCase().startsWith(tempQ)
            );
            if (tempFiltered.length > 0) {
              this.currentToken = alt;
              this.tokenStartIndex = prevStart;
              this.showVariableDropdown = true;
            }
          }
        }
      }
      if (this.showVariableDropdown) {
        const tokenEnd = this.tokenStartIndex + this.currentToken.length;
        if (this.caretOffset !== tokenEnd) {
          this.showVariableDropdown = false;
        }
      }
      if (this.showVariableDropdown) {
        this.selectedIndex = 0;
        this.updateDropdownPosition();
      } else {
        this.selectedIndex = -1;
      }
      this.prompt = this.getPromptText();
      this.plainText = plain;
    },
    createChip(item) {
      const chip = document.createElement('span');
      chip.setAttribute('contenteditable', 'false');
      chip.className = 'chip';
      chip.setAttribute('data-name', item.name);
      chip.innerHTML = `<span class="chip-label">${
        item.displayName || item.name
      }</span><span class="chip-remove" contenteditable="false">×</span>`;

      chip.querySelector('.chip-remove').addEventListener('click', () => {
        const parent = chip.parentNode;
        if (!parent) return;
        parent.replaceChild(document.createTextNode(' '), chip);
        this.$nextTick(() => this.onInput());
      });

      return chip;
    },
    renderDefaultValue(text, isActiveInput = true) {
      if (!text) return;
      this.$refs.editable.innerHTML = ''; // clear before rendering

      const parts = text.split(/(\{[^}]+\})/g);
      const range = document.createRange();
      const sel = window.getSelection();

      // set range at start of editable
      range.setStart(this.$refs.editable, 0);
      range.collapse(true);

      parts.forEach((part) => {
        if (part.startsWith('{') && part.endsWith('}')) {
          const name = part.slice(1, -1);
          const variable = this.getInputVariables.find((v) => v.name === name);
          const suggestId = getElementSuggetionById(
            name,
            this.getInputVariables
          );
          if (variable || suggestId) {
            const chip = this.createChip(variable || suggestId);
            range.insertNode(chip);

            // move range after chip
            range.setStartAfter(chip);
            range.collapse(true);
            return;
          }
        }
        const textNode = document.createTextNode(part);
        range.insertNode(textNode);

        // move range after text
        range.setStartAfter(textNode);
        range.collapse(true);
      });
      // reset caret to end of all content
      sel.removeAllRanges();
      sel.addRange(range);
      if (isActiveInput) this.onInput(); // update prompt state
    },
    scrollToSelectedVariable() {
      this.$nextTick(() => {
        const selectedVariable = document.querySelector(
          '.suggest-variable.selected'
        );
        if (selectedVariable) {
          selectedVariable.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      });
    },
    onKeydown(e) {
      if (this.showVariableDropdown && this.filteredVariables.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.selectedIndex = Math.min(
            this.selectedIndex + 1,
            this.filteredVariables.length - 1
          );
          this.scrollToSelectedVariable();
          return;
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
          this.scrollToSelectedVariable();
          return;
        }
      }
      // accept suggestion with Enter when dropdown is visible
      if (
        e.key === 'Enter' &&
        this.showVariableDropdown &&
        this.filteredVariables.length > 0
      ) {
        e.preventDefault();
        const selectedItem =
          this.filteredVariables[this.selectedIndex] ||
          this.filteredVariables[0];
        this.insertVariableAtToken(selectedItem);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey && !this.showVariableDropdown) {
        e.preventDefault();
        this.$refs.editable.blur();
        // Otherwise, submit
        this.submitPrompt();
        return;
      }
    },
    onKeyup(e) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        this.onInput();
      }
    },
    onSuggestionClick(item) {
      this.insertVariableAtToken(item);
    },
    onFocus() {
      this.isFocused = true;
    },
    onBlur() {
      this.hideDropdownTimer = setTimeout(() => {
        this.showVariableDropdown = false;
        this.isFocused = false;
      }, 200);
    },
    getTextNodeAtPosition(root, index) {
      const walker = document.createTreeWalker(
        root,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      let node = walker.nextNode();
      let cum = 0;
      while (node) {
        const len = node.textContent.length;
        if (cum + len >= index) {
          return { node, offset: index - cum };
        }
        cum += len;
        node = walker.nextNode();
      }
      const last = root.appendChild(document.createTextNode(''));
      return { node: last, offset: 0 };
    },
    insertVariableAtToken(item) {
      let startIndex = Math.max(0, this.tokenStartIndex || 0);
      let endIndex = this.getCaretCharacterOffset();

      const plain = this.getPlainText();
      const postfix = plain.slice(endIndex);
      const nextSpace = postfix.search(/\s/);
      const wordEnd = nextSpace !== -1 ? endIndex + nextSpace : plain.length;
      endIndex = wordEnd;
      const start = this.getTextNodeAtPosition(this.$refs.editable, startIndex);
      const end = this.getTextNodeAtPosition(this.$refs.editable, endIndex);
      const range = document.createRange();
      try {
        range.setStart(start.node, start.offset);
        range.setEnd(end.node, end.offset);
      } catch {
        return;
      }
      range.deleteContents();
      this.insertChip(item, range);
    },
    insertChip(item, range, skipCaret = false) {
      const chip = this.createChip(item);
      range.insertNode(chip);
      const spaceNode = document.createTextNode(' ');
      chip.parentNode.insertBefore(spaceNode, chip.nextSibling);

      if (!skipCaret) {
        // only move caret during user input, not default rendering
        const sel = window.getSelection();
        const newRange = document.createRange();
        newRange.setStart(spaceNode, 1);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
      }

      this.currentToken = '';
      this.showVariableDropdown = false;
      this.$nextTick(() => this.onInput());
    },

    updateDropdownPosition() {
      const sel = window.getSelection();
      if (!sel.rangeCount) return;
      const range = sel.getRangeAt(0).cloneRange();
      range.collapse(true);
      const rects = range.getClientRects();
      if (rects.length > 0) {
        const rect = rects[0];
        const containerRect = this.$refs.editable.getBoundingClientRect();
        let left = rect.left - containerRect.left;
        const dropdownWidth = 220;
        const containerWidth = containerRect.width;

        if (left + dropdownWidth > containerWidth) {
          left = containerWidth - dropdownWidth;
          if (left < 0) left = 0;
        }
        this.variableDropdownPos = {
          top: rect.bottom - containerRect.top,
          left: left,
        };
      }
    },

    // copy / paste
    onPaste(e) {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData('text');
      const sel = window.getSelection();
      if (!sel.rangeCount) return;
      const range = sel.getRangeAt(0);

      // Delete current selection before inserting
      range.deleteContents();

      const frag = document.createDocumentFragment();
      const parts = text.split(/(\{[^}]+\})/g);

      parts.forEach((part) => {
        if (part.startsWith('{') && part.endsWith('}')) {
          const name = part.slice(1, -1);
          const variable = this.getInputVariables.find((v) => v.name === name);
          const suggestId = getElementSuggetionById(
            name,
            this.getInputVariables
          );
          if (variable || suggestId) {
            frag.appendChild(this.createChip(variable || suggestId));
            return;
          }
        }
        frag.appendChild(document.createTextNode(part));
      });

      range.insertNode(frag);

      // Move caret after inserted content
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);

      this.onInput();
    },
    onCopy(e) {
      const text = this.getPromptText();
      e.clipboardData.setData('text/plain', text);
      e.preventDefault();
    },
    onCut(e) {
      const text = this.getPromptText(); // same as copy
      e.clipboardData.setData('text/plain', text);
      e.preventDefault();

      // remove selected content manually
      const sel = window.getSelection();
      if (!sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      range.deleteContents();

      this.onInput(); // update prompt state
    },
    setSuggetionDropdown() {
      const element = this.$refs.suggetionsWrapper;

      const rect = element.getBoundingClientRect();

      if (rect.bottom < window.innerHeight) {
        this.suggetionDropdownPosStyle = {};
      } else {
        this.suggetionDropdownPosStyle = {
          top: 0 - rect.height + 'px',
          left: 'unset',
        };
      }
    },
  },
  computed: {
    ...mapGetters(['getFormPromptSuggestions']),
    filteredVariables() {
      const q = (this.currentToken || '').toLowerCase();
      if (!q) return [];
      if (q === 'this') {
        const suggestId = getElementSuggetionById(
          this.selectedId,
          this.getInputVariables
        );
        if (suggestId) return [suggestId];
      }
      const startsWith = this.getInputVariables.filter((v) =>
        v.label.toLowerCase().startsWith(q)
      );
      const includes = this.getInputVariables.filter(
        (v) =>
          !v.label.toLowerCase().startsWith(q) &&
          v.label.toLowerCase().includes(q)
      );

      return [...startsWith, ...includes];
    },

    getInputVariables() {
      return getInputVariableList(this.elements);
    },
    isSuggestionDropdownVisible() {
      return (
        this.isFocused &&
        !this.plainText &&
        Object.keys(this.getCurrentFormSuggetions)?.length &&
        (this.getCurrentFormSuggetions['Formate']?.length ||
          this.getCurrentFormSuggetions['Konditionen/Berechnungen']?.length)
      );
    },
    getCurrentFormSuggetions() {
      const suggestions =
        this.getFormPromptSuggestions?.[this.processId]?.[this.taskId];

      if (!suggestions || !Object.keys(suggestions).length) return {};

      const logicSuggtions = (suggestions?.['logic_suggestions'] ?? []).map(
        (suggestion) => ({
          caption: suggestion,
          value: suggestion,
        })
      );
      const styleSuggtions = (suggestions?.['style_suggestions'] ?? []).map(
        (suggestion) => ({
          caption: suggestion,
          value: suggestion,
        })
      );

      return {
        Formate: styleSuggtions,
        'Konditionen/Berechnungen': logicSuggtions,
      };
    },
  },

  mounted() {
    // set position of suggetion dropdown wrappper

    setTimeout(() => {
      this.setSuggetionDropdown();
    }, 10);
  },
  beforeDestroy() {
    if (this.hideDropdownTimer) clearTimeout(this.hideDropdownTimer);
  },

  components: {
    LoadingSpinner,
    AskAiSuggetionDropdown,
  },
};
</script>

<style scoped>
.ask-ai-input-wrapper {
  border: 0.5px solid #dee3e9;
  padding: 12px;
  border-radius: 10px;
  margin-top: 16px;
  position: relative;
}
.ask-ai-input-wrapper .title {
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 18px;
  color: #000;
  gap: 6px;
  font-weight: 500;
}

.ask-ai-input-wrapper .title img {
  width: 18px;
}
.ask-ai-input-wrapper .prompt-wrapper {
  position: relative;
  margin-top: 16px;
  width: 100%;
}
.ask-ai-input-wrapper .prompt-wrapper .submit-btn {
  position: absolute;
  bottom: 12px;
  right: 10px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: black;
  border: 1px solid black;
  z-index: 1;
}

.ask-ai-input-wrapper .prompt-wrapper .submit-btn:disabled {
  background-color: #ababab;
  border: 1px solid #ababab;
}

.ask-ai-input-wrapper .prompt-wrapper .submit-btn svg {
  font-size: 25px;
}
.ask-ai-input-wrapper .editable {
  min-height: 48px;
  border: 1px solid #cbcbcb;
  padding: 14px;
  border-radius: 10px;
  width: 100%;
  min-height: 66px;
  max-height: 250px;
  white-space: pre-wrap;
  word-break: break-word;
  caret-color: #111;
  font-size: 15px;
  color: black;
  overflow: auto;
}

.ask-ai-input-wrapper .loading-spinner {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: white;
  overflow: hidden;
  border: 2px solid black;
  border-radius: 10px;
}
.ask-ai-input-wrapper .editable::placeholder {
  color: #9b9b9b;
}
.ask-ai-input-wrapper .editable:empty:before {
  content: attr(data-placeholder);
  color: #aaa;
  pointer-events: none;
  font-size: 14px;
}

::v-deep(.chip-label) {
  pointer-events: none;
  user-select: none;
}

.ask-ai-input-wrapper .dropdown {
  position: absolute;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 2px;
  width: 220px;
  max-height: 180px;
  overflow-y: auto;
  z-index: 50;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  padding: 6px 0;
  list-style: none;
  margin: 0;
  padding: 5px;
}

.ask-ai-input-wrapper .dropdown li {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  color: #000;
  border-radius: 3px;
  font-family: 'OpenSans-Regular';
  letter-spacing: -0.02em;
  font-weight: 400;
}

.ask-ai-input-wrapper .dropdown li:hover {
  background: #f3f4f6;
}
.ask-ai-input-wrapper .dropdown li.selected {
  background-color: #f4f4f4;
}

::v-deep(.chip) {
  background: #343331;
  color: white;
  border-radius: 4px;
  padding: 2px 4px;
  display: inline-block;
  margin-right: 4px;
  margin-bottom: 2px;
}

::v-deep(.chip-remove) {
  cursor: pointer;
  padding-left: 4px;
  font-size: 16px;
}

.ask-ai-input-wrapper .suggetions-wrapper {
  position: absolute;
  z-index: 10;
  width: calc(100% - 24px);
}
</style>
